import { useContext, useState } from 'react';
import { PersonAdd, SwapHoriz } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, LinearProgress, Typography } from '@mui/material';
import { MAX_DELEGATION_DEPTH } from 'common/constants/delegation';
import { DelegatedRequest } from 'common/types/delegation';
import { TaskContext } from 'context/TaskContext';
import { TaskContextType } from 'context/TaskContext/types';
import DelegateModal from '../DelegateModal/DelegateModal';

const DelegationSection = () => {
  const { delegationInfo, delegatedRequests, task, validateToken } = useContext(
    TaskContext
  ) as TaskContextType;
  const [delegateModalOpen, setDelegateModalOpen] = useState(false);
  const [redelegateTarget, setRedelegateTarget] = useState<DelegatedRequest | null>(null);

  const handleOpenDelegateModal = () => {
    setRedelegateTarget(null);
    setDelegateModalOpen(true);
  };

  const handleOpenRedelegateModal = (delegation: DelegatedRequest) => {
    setRedelegateTarget(delegation);
    setDelegateModalOpen(true);
  };

  const handleCloseModal = () => {
    setDelegateModalOpen(false);
    setRedelegateTarget(null);
  };

  const handleDelegationSuccess = () => {
    handleCloseModal();
    validateToken();
  };

  const isTaskComplete = task?.task?.is_done;
  const delegationDepth = task?.task?.delegation_depth ?? 0;
  const canDelegate = delegationDepth < MAX_DELEGATION_DEPTH;

  // Calculate combined progress
  const getProgress = (delegation: DelegatedRequest) => {
    const totalItems = delegation.total_files_count + delegation.total_questions_count;
    const completedItems = delegation.uploaded_files_count + delegation.answered_questions_count;

    if (totalItems === 0) {
      return 0;
    }
    return (completedItems / totalItems) * 100;
  };

  const getProgressLabel = (delegation: DelegatedRequest) => {
    const hasFiles = delegation.total_files_count > 0;
    const hasQuestions = delegation.total_questions_count > 0;

    if (hasFiles && hasQuestions) {
      return `${delegation.uploaded_files_count}/${delegation.total_files_count} docs, ${delegation.answered_questions_count}/${delegation.total_questions_count} questions`;
    } if (hasQuestions) {
      return `${delegation.answered_questions_count} / ${delegation.total_questions_count} questions`;
    }
    return `${delegation.uploaded_files_count} / ${delegation.total_files_count} documents`;
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Show delegation source if this is a delegated request */}
      {delegationInfo.is_delegated && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.dark">
            This request was delegated to you by{' '}
            <strong>{delegationInfo.delegator_name || delegationInfo.delegator_email}</strong>
          </Typography>
        </Box>
      )}

      {/* Delegated requests list */}
      {delegatedRequests.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Delegated To
          </Typography>
          {delegatedRequests.map(delegation => (
            <Card key={delegation.id} variant="outlined" sx={{ mb: 1 }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {delegation.delegatee_name}
                      </Typography>
                      <Chip
                        label={delegation.is_complete ? 'Complete' : 'In Progress'}
                        size="small"
                        color={delegation.is_complete ? 'success' : 'default'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {delegation.delegatee_email}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Progress
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getProgressLabel(delegation)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={getProgress(delegation)}
                        sx={{ height: 6, borderRadius: 1 }}
                      />
                    </Box>
                  </Box>
                  {!isTaskComplete && (
                    <Button
                      size="small"
                      startIcon={<SwapHoriz />}
                      onClick={() => handleOpenRedelegateModal(delegation)}
                      sx={{ ml: 2 }}
                    >
                      Reassign
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Delegate button */}
      {!isTaskComplete && canDelegate && (
        <Button
          variant="outlined"
          startIcon={<PersonAdd />}
          onClick={handleOpenDelegateModal}
          size="small"
        >
          Delegate to Someone
        </Button>
      )}

      {/* Delegate/Redelegate Modal */}
      <DelegateModal
        open={delegateModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleDelegationSuccess}
        redelegateTarget={redelegateTarget}
      />
    </Box>
  );
};

export default DelegationSection;
